/**
 * StaffAccessPanel — admin Settings → Staff & Access sub-tab.
 *
 * Read-only account-management surface for OPERATIONAL accounts only
 * (admin / dispatcher / staff / support / operations — never drivers or
 * passengers). Shows name, email, role, status, created date and last login,
 * with a "Send reset link" action per account that reuses the existing
 * hardened POST /admin/users/{id}/send-reset-link flow.
 *
 * No create/invite in this sprint — this is purely viewing accounts and
 * triggering password recovery.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ShieldCheck, KeyRound, Search, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const roleBadgeClass = (role) => {
  const r = (role || "").toLowerCase().trim();
  if (r === "admin") return "bg-[#002FA7] text-white";
  if (r === "dispatcher") return "bg-[#10B981] text-white";
  return "bg-secondary text-foreground border border-border";
};

const StatusPill = ({ s }) => {
  if (s.email_verified) {
    return (
      <span className="inline-flex items-center gap-1 text-[#065F46]" data-testid={`staff-status-${s.id}`}>
        <CheckCircle2 className="w-3.5 h-3.5" /> Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[#92400E]" data-testid={`staff-status-${s.id}`}>
      <XCircle className="w-3.5 h-3.5" /> Unverified
    </span>
  );
};

export const StaffAccessPanel = () => {
  const [staff, setStaff] = useState(null);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [sendingId, setSendingId] = useState(null);

  const fetchStaff = async () => {
    setErr(null);
    try {
      const { data } = await api.get("/admin/staff");
      setStaff(data);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  const sendResetLink = async (s) => {
    setSendingId(s.id);
    try {
      const { data } = await api.post(`/admin/users/${s.id}/send-reset-link`);
      if (data?.delivered) {
        toast.success(data.message || `Password reset link sent to ${s.email}.`);
      } else if (data?.reset_url) {
        // Email not delivered — copy the secure fallback link to the clipboard.
        try { await navigator.clipboard.writeText(data.reset_url); } catch { /* ignore */ }
        toast.warning(`${data.message || "Email not delivered."} Link copied to clipboard.`);
      } else {
        toast.warning(data?.message || "Email not delivered.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!staff) return [];
    const q = query.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.role || "").toLowerCase().includes(q)
    );
  }, [staff, query]);

  return (
    <div className="space-y-4" data-testid="staff-access-panel">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-muted-foreground" />
          <div>
            <div className="label-mini text-muted-foreground">ACCOUNT MANAGEMENT</div>
            <h3 className="font-heading text-xl font-bold tracking-tight">Staff &amp; Access</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Operational accounts (admin, dispatcher, staff, support, operations). Drivers and
              customers are managed under Fleet and Customers. Send a password reset link to recover
              any account — no passwords are ever shown here.
            </p>
          </div>
        </div>
        <button
          onClick={fetchStaff}
          className="text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors flex items-center gap-1.5"
          data-testid="staff-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email or role…"
          className="w-full pl-9 pr-3 py-2 border border-border text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          data-testid="staff-search-input"
        />
      </div>

      {err && (
        <div className="border-2 border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="staff-error">
          {err}
        </div>
      )}

      {staff === null ? (
        <div className="text-sm text-muted-foreground" data-testid="staff-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="staff-empty">
          {staff.length === 0 ? "No operational accounts found." : "No accounts match your search."}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm" data-testid="staff-table">
            <thead>
              <tr className="text-left label-mini text-muted-foreground border-b border-border bg-secondary/30">
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">Email</th>
                <th className="px-4 py-2 font-semibold">Role</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Created</th>
                <th className="px-4 py-2 font-semibold">Last login</th>
                <th className="px-4 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-2.5 font-semibold whitespace-nowrap">{s.name || "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground" data-testid={`staff-email-${s.id}`}>{s.email || "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm font-bold ${roleBadgeClass(s.role)}`} data-testid={`staff-role-${s.id}`}>
                      {s.role || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5"><StatusPill s={s} /></td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground" data-testid={`staff-last-login-${s.id}`}>{fmtDateTime(s.last_login_at)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => sendResetLink(s)}
                      disabled={sendingId === s.id}
                      className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-border hover:bg-secondary transition-colors disabled:opacity-60"
                      data-testid={`staff-send-reset-${s.id}`}
                      title="Send password reset link"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                      {sendingId === s.id ? "Sending…" : "Send reset link"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StaffAccessPanel;
